/**
 * Logging utility functions for posting logs to Lambda endpoint
 */

interface LogData {
  message: string;
  logId: string;
  data?: Record<string, any>;
  [key: string]: any;
}

interface LogResponse {
  message: string;
  logId: string;
  timestamp: string;
  level: string;
}

/**
 * Gets the userId from localStorage by finding a key ending with "LastAuthUser"
 * @returns {string | null} The userId value or null if not found
 */
const getUserIdFromLocalStorage = (): string | null => {
  try {
    // Find all localStorage keys that end with "LastAuthUser"
    const matchingKeys = Object.keys(localStorage).filter((key) => key.endsWith('LastAuthUser'));

    if (matchingKeys.length === 0) {
      console.warn('No localStorage key ending with "LastAuthUser" found');
      return null;
    }

    // Use the first matching key
    const authKey = matchingKeys[0];
    const userId = localStorage.getItem(authKey);

    if (!userId) {
      console.warn(`Found key "${authKey}" but it has no value`);
      return null;
    }

    return userId;
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return null;
  }
};

/**
 * Posts log data to the logging Lambda endpoint
 * @param message - The log message
 * @param logId - Unique identifier for the log entry
 * @param data - Optional additional data object to log
 * @returns Promise with response data or null if failed
 */
export const postLogToLambda = async (message: string, logId: string, data?: Record<string, any>): Promise<LogResponse | null> => {
  try {
    // Get userId from localStorage
    const userId = getUserIdFromLocalStorage();

    // Get login time from localStorage
    const loginTime = localStorage.getItem('loginTime');
    const deviceKey = localStorage.getItem('deviceKey');
    const lastActivityTime = localStorage.getItem('lastActivityTimeMs');
    if (data) {
      data.xdevicekey = deviceKey;
      data.lastActivityTime = lastActivityTime;
    } else {
      data = { xdevicekey: deviceKey, lastActivityTime };
    }
    // Prepare the log data with userId if available
    const logData: LogData = {
      message,
      logId,
      timestamp: new Date().toISOString(),
      level: process.env.REACT_APP_ENV || 'info',
    };

    // Add data if provided
    if (data) {
      logData.data = data;
    }

    // Add userId if available
    if (userId) {
      logData.userId = userId;
    }

    // Add login time if available
    if (loginTime) {
      if (!logData.data) {
        logData.data = {};
      }
      logData.data.loggedInTime = loginTime;
    }

    const response = await fetch('https://2e4wfp6zlm5tus6x37a3nnocay0kfffq.lambda-url.ap-south-1.on.aws/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logData),
    });

    if (!response.ok) {
      console.warn('Log posting failed:', response.status, response.statusText);
      return null;
    }

    const result: LogResponse = await response.json();
    console.log('Log posted successfully:', result);
    return result;
  } catch (error) {
    // Silently handle errors - function should not throw outside
    console.warn('Error posting log to Lambda:', error instanceof Error ? error.message : 'Unknown error');
    return null;
  }
};
